import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomTextField from "../../CustomTextField";
import { Box, Button, Dialog, DialogActions, DialogContent, InputAdornment, MenuItem, IconButton, Typography, Grid } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DatePickerWrapper from "../../DatePicker/DatePickerWrapper";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ko } from 'date-fns/locale';

const UserDialog = ({ open, onClose, userData = null, onSuccess }) => {
  const isEditMode = Boolean(userData);
  
  const [formValues, setFormValues] = useState({
    id: "",
    name: "",
    password: "",
    confirmPassword: "",
    email: "",
    joinDate: "",
    team: "1",
    userType: "user",
  });
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [confirmPasswordVisibility, setConfirmPasswordVisibility] = useState(false);

  useEffect(() => {
    if (userData) {
      setFormValues({
        id: userData.login_id,
        name: userData.user_name,
        password: "",
        confirmPassword: "",
        email: userData.email,
        joinDate: userData.join_date ? new Date(userData.join_date) : "",
        team: userData.team_id || "1",
        userType: userData.user_type?.toLowerCase() || "user",
      });
    } else {
      setFormValues({
        id: "",
        name: "",
        password: "",
        confirmPassword: "",
        email: "",
        joinDate: "",
        team: "1",
        userType: "user",
      });
    }
  }, [userData, open]);

  const handleChange = (prop) => (event) => {
    setFormValues({ ...formValues, [prop]: event.target.value });
  };

  const handleDateChange = (date) => {
    setFormValues({ ...formValues, joinDate: date });
  };

  const handleClickShowPassword = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  const handleClickConfirmPassShow = () => {
    setConfirmPasswordVisibility(!confirmPasswordVisibility);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { id, password, name, email, joinDate, team, userType } = formValues;

    if (!name || !email || (!isEditMode && (!id || !password))) {
      alert('모든 필수 필드를 입력해주세요.');
      return;
    }

    if (password && password !== formValues.confirmPassword) {
      alert('패스워드가 일치하지 않습니다. 확인 후 다시 입력해주세요.');
      return;
    }

    const userData = {
      id,
      user_name: name,
      email,
      team_id: team,
      user_type: userType,
      join_date: joinDate
    };

    if (password) {
      userData.password = password;
    }

    const apiCall = isEditMode
      ? axios.put(`/backend/project/updateUser/${userData.user_no}`, userData)
      : axios.post("/backend/project/createUser", userData);

    apiCall
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch((err) => {
        if (err.response?.status === 409) {
          alert('이미 존재하는 로그인 아이디입니다.');
        } else {
          console.error(err);
          alert(`유저 ${isEditMode ? '수정' : '생성'} 오류`);
        }
      });
  };

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
      <DatePickerWrapper>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 8 }}>
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {isEditMode ? '유저 수정' : '유저 생성'}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {isEditMode ? '유저 정보 수정' : '유저 정보 입력'}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  size="small"
                  name="id"
                  value={formValues.id}
                  autoComplete="off"
                  label="아이디"
                  onChange={handleChange("id")}
                  placeholder="아이디"
                  required={!isEditMode}
                  disabled={isEditMode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                  fullWidth 
                  size="small"
                  name="name" 
                  value={formValues.name} 
                  autoComplete="off" 
                  label="사용자 이름" 
                  placeholder="사용자 이름" 
                  onChange={handleChange("name")} 
                  required 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  size="small"
                  label="패스워드"
                  value={formValues.password}
                  id="basic-password"
                  onChange={handleChange("password")}
                  placeholder={isEditMode ? "패스워드 변경시에만 입력" : "············"}
                  type={passwordVisibility ? "text" : "password"}
                  required={!isEditMode}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" size="small" onClick={handleClickShowPassword} onMouseDown={(e) => e.preventDefault()}>
                          {passwordVisibility ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  size="small"
                  label="패스워드 확인"
                  value={formValues.confirmPassword}
                  id="confirm-password"
                  onChange={handleChange("confirmPassword")}
                  placeholder={isEditMode ? "패스워드 변경시에만 입력" : "············"}
                  type={confirmPasswordVisibility ? "text" : "password"}
                  required={!isEditMode}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" size="small" onClick={handleClickConfirmPassShow} onMouseDown={(e) => e.preventDefault()}>
                          {confirmPasswordVisibility ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                  fullWidth 
                  size="small"
                  type="email" 
                  label="Email" 
                  placeholder="ese-tech@ese-tech.com" 
                  value={formValues.email} 
                  onChange={handleChange("email")} 
                  required 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ position: 'relative', zIndex: 1000 }}>
                  <DatePicker
                    selected={formValues.joinDate}
                    onChange={handleDateChange}
                    dateFormat="yyyy년 MM월 dd일"
                    locale={ko}
                    customInput={
                      <CustomTextField 
                        fullWidth 
                        size="small"
                        label="입사일" 
                      />
                    }
                    portalId="root"
                    popperContainer={({ children }) => (
                      <div style={{ position: 'relative', zIndex: 1500 }}>{children}</div>
                    )}
                    placeholderText="날짜를 선택하세요"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                  select 
                  fullWidth 
                  size="small"
                  label="팀" 
                  value={formValues.team} 
                  onChange={handleChange("team")}
                >
                  <MenuItem value="1">생산계획팀</MenuItem>
                  <MenuItem value="2">디지털트윈팀</MenuItem>
                  <MenuItem value="3">모델링팀</MenuItem>
                  <MenuItem value="4">컨설팅팀</MenuItem>
                  <MenuItem value="5">기술영업</MenuItem>
                </CustomTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                  select 
                  fullWidth 
                  size="small"
                  label="권한" 
                  value={formValues.userType} 
                  onChange={handleChange("userType")}
                >
                  <MenuItem value="admin">관리자</MenuItem>
                  <MenuItem value="user">유저</MenuItem>
                </CustomTextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", p: 8, pt: 0 }}>
            <Button variant="contained" type="submit">
              {isEditMode ? '수정' : '생성'}
            </Button>
            <Button variant="tonal" color="secondary" onClick={onClose}>
              취소
            </Button>
          </DialogActions>
        </form>
      </DatePickerWrapper>
    </Dialog>
  );
};

export default UserDialog; 